/* eslint-disable import/no-unresolved */
import { useState, useMemo, useRef } from 'react';
import { Subscription } from 'rxjs';
import { IWidget } from '@sky-it-livedata-libraries/livedata-widget';
import LivedataDataCardsWidgets, {
  ILivedataDataCardsWidgetsConfig,
} from '@sky-it-livedata/livedata-data-cards-widgets';

import { getServiceConfiguration } from '../../utils/service-config';
import { isEmptyObject, isValidObject } from '../../utils/props-validator';
import { IGenericObject } from '../../typings/common-types';

export interface IUseServiceParams {
  live?: boolean;
  initialValue?: IGenericObject;
  query: IGenericObject;
  widget: IWidget;
}
interface IUseServiceReturnedValues {
  model: IGenericObject | null;
  error: number;
}

interface IUseService {
  (params: IUseServiceParams): IUseServiceReturnedValues;
}

const serviceConfig = (): ILivedataDataCardsWidgetsConfig => {
  try {
    const { apikey: apiKey, endpoint, realtimeEndpoint, realtimeHost } = getServiceConfiguration();
    return {
      apiKey,
      endpoint,
      realtime: true,
      realtimeEndpoint,
      realtimeHost,
    };
  } catch (error) {
    console.error('[useService] Error in Service configuration', error); // eslint-disable-line no-console
    return {
      apiKey: 'fallback-apikey',
      endpoint: 'fallback-endpoint',
    };
  }
};

const service = new LivedataDataCardsWidgets(serviceConfig());

export const useService: IUseService = ({
  /* istanbul ignore next: default value */ live = false,
  /* istanbul ignore next: default value */ initialValue = null,
  /* istanbul ignore next: default value */ query = {},
  widget,
}) => {
  const widgetRef = useRef(widget);
  const subscriptionRef = useRef(new Subscription());
  const [queryState] = useState(query);
  const [model, setModel] = useState(initialValue);
  const [error, setError] = useState(0);
  const widgetCurrent: IWidget = widgetRef.current;

  /* Query Hook */
  useMemo(() => {
    if (!model || isEmptyObject(model)) {
      const doQuery = async () => {
        try {
          const newData: IGenericObject = await service.query(widgetCurrent, queryState);
          if (isValidObject(newData)) {
            setModel({ query: newData });
          }
        } catch (error) {
          console.error('[useService] Error in QUERY', { error }); // eslint-disable-line no-console
          setError(500);
        }
      };

      doQuery();
    }
  }, [model, queryState, widgetCurrent]);

  /* Subscription Hook   */
  useMemo(() => {
    if (live) {
      // To avoid CRASH when LIVE=TRUE but WIDGET has NO SUBSCRIPTION defined
      if (widgetCurrent.subscription) {
        subscriptionRef.current = service.subscription(widgetCurrent, queryState).subscribe({
          next: (dataSubscription: IGenericObject) => {
            if (isValidObject(dataSubscription)) {
              setModel({ subscription: dataSubscription });
            }
          },
          error: (error) => {
            console.error('[useService] Error in SUBSCRIPTION', { error }); // eslint-disable-line no-console
            setError(500);
          },
        });
      } else {
        console.warn(`Widget '${widgetCurrent.name}' has LIVE=TRUE without a SUBSCRIPTION defined`); // eslint-disable-line no-console
      }
    }

    return () => subscriptionRef.current.unsubscribe;
  }, [live, queryState, widgetCurrent, subscriptionRef]);

  return { model, error };
};
